import { Component, OnInit, Inject, signal, ChangeDetectionStrategy } from '@angular/core';
import {
  MatDialog,
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogTitle,
  MatDialogClose,
  MatDialogContent,
} from '@angular/material/dialog';
import { EditRideComponent } from '../edit-ride/edit-ride.component';
import { CopyRideComponent } from '../copy-ride/copy-ride.component';
import {
  NotificationsParticipants,
  Ride,
  RideLocation,
  User,
  UserInRide,
} from 'src/app/interfaces';
import { Vehicle, Particularity } from 'src/app/interfaces';
import { DeleteRideComponent } from '../edit-ride/dialogs/delete-ride/delete-ride.component';
import { CopyUpdateComponent } from '../copy-update/copy-update.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RideEditsComponent } from '../ride-edits/ride-edits.component';
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import moment from 'moment';
import { dataStore, db } from 'src/app/app.component';
import {
  calculateAutomaticRideOccupancy,
  getTimeSlots,
  getUserById,
  getRideCopyVehicleError,
  onMapsClick,
  updateRideToV2,
} from '../../helpers/ride-helper';
import {
  deleteRideNotificationParticipant,
  getOrganisationNotificationSettings,
  removeFromReminderQueue,
} from '../../helpers/notification-helper';
import { NotifyUsersRideDialog } from '../edit-ride/dialogs/notify-users-ride/notify-users-ride.component';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatCard } from '@angular/material/card';
import { MatChipSet, MatChip } from '@angular/material/chips';
import { DatePipe } from '@angular/common';
import { valueToDate } from 'src/app/globals';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';

export interface DialogData {
  ride: Ride;
  vehicle: Vehicle;
}

@Component({
  selector: 'app-ride-details',
  templateUrl: './ride-details.component.html',
  styleUrls: ['./ride-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    CdkDrag,
    CdkDragHandle,
    MatIcon,
    MatIconButton,
    MatTooltip,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatDialogClose,
    CdkScrollable,
    MatDialogContent,
    MatCard,
    MatButton,
    MatChipSet,
    MatChip,
    DatePipe,
  ],
})
export class RideDetailsComponent implements OnInit {
  userId = signal<string>(dataStore.rootUser.id);
  userEmail = signal<string>(dataStore.rootUser.email);
  users = signal<UserInRide[]>([]);
  particularities = signal<Particularity[]>(dataStore.selectedOrganisation.particularities || []);
  driver = signal<User | null>(null);
  coDriver = signal<User | null>(null);
  organisationId = signal<string>(dataStore.selectedOrganisation.id);
  ignoreDriverStatus = signal<boolean>(false);
  showRideProduct = signal<boolean>(true);
  characteristics = signal<string[]>([]);
  organisationNotificationSettings = signal<{
    email: NotificationsParticipants;
    sms: NotificationsParticipants;
  } | null>(null);
  deleting = signal(false);
  showDriverPlanning = signal<boolean>(dataStore.paidFunctionalities.usesDriverPlanning);
  characteristicsStatus = signal<boolean>(false);
  timeSlots = signal<{ time: any; locations: RideLocation[] }[]>([]);
  automaticOccupancy = signal<ReturnType<typeof calculateAutomaticRideOccupancy>>([]);
  ride = signal<Ride>(this.data.ride);
  resolvedVehicle = signal<Vehicle | null>(this.data.vehicle ?? null);
  vehicleUnavailable = signal(false);

  getUserById = getUserById;
  valueToDate = valueToDate;
  moment = moment;
  dataStore = dataStore;
  onMapsClick = onMapsClick;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<RideDetailsComponent>,
    private snackBar: MatSnackBar,
  ) {}

  async ngOnInit() {
    const ride =
      this.data.ride.rideVersion === 2 ? this.data.ride : await updateRideToV2(this.data.ride);
    this.ride.set(ride);
    await this.resolveVehicle(ride);

    this.timeSlots.set(getTimeSlots(this.ride().locationsOrder ?? []));
    console.log('RideDetailsComponent data:', this.data);
    if (this.ride().users) {
      this.users.set(Object.values(this.ride().users));
    }
    this.updateAutomaticOccupancy();

    if (this.ride().characteristics) {
      for (const characteristic of this.ride().characteristics) {
        const index = this.characteristics().indexOf(characteristic);
        if (index === -1) {
          this.characteristics.update((chars) => [...chars, characteristic]);
        }
      }
    }

    this.organisationNotificationSettings.set(getOrganisationNotificationSettings());
    this.characteristicsStatus.set(dataStore.generalSettings.characteristicsStatus ?? false);
    this.ignoreDriverStatus.set(dataStore.generalSettings.ignoreDriverStatus ?? false);
    this.showRideProduct.set(dataStore.generalSettings.showRideProduct ?? true);

    if (this.ride().driverId) {
      const driverDoc = await getDoc(
        doc(db, `organisations/${this.organisationId()}/users/${this.ride().driverId}`),
      );
      this.driver.set(driverDoc.data() as User);
    }
    if (this.ride().coDriverId) {
      const coDriverDoc = await getDoc(
        doc(db, `organisations/${this.organisationId()}/users/${this.ride().coDriverId}`),
      );
      this.coDriver.set(coDriverDoc.data() as User);
    }
  }

  isPassengerPaid(user: UserInRide): boolean {
    return user.paid === true;
  }

  updateAutomaticOccupancy() {
    if (!this.ride().automaticallyPlanned) {
      this.automaticOccupancy.set([]);
      return;
    }

    this.automaticOccupancy.set(
      calculateAutomaticRideOccupancy(
        this.ride().locationsOrder ?? [],
        this.resolvedVehicle()?.seats ?? 0,
        this.ride().coDriverId,
        this.users(),
        this.particularities(),
      ),
    );
  }

  private async resolveVehicle(ride: Ride): Promise<void> {
    if (this.resolvedVehicle() || !ride.vehicleId || !ride.vehicleRef) {
      this.vehicleUnavailable.set(!this.resolvedVehicle() && !!ride.vehicleId);
      return;
    }

    const vehicleRefPath =
      typeof ride.vehicleRef === 'string' ? ride.vehicleRef : ride.vehicleRef?.path;
    const normalizedPath = vehicleRefPath?.replace(/^\/+/, '');
    if (!normalizedPath) {
      this.vehicleUnavailable.set(true);
      return;
    }

    try {
      const vehicleSnapshot = await getDoc(doc(db, normalizedPath));
      if (!vehicleSnapshot.exists() || vehicleSnapshot.id !== ride.vehicleId) {
        this.vehicleUnavailable.set(true);
        return;
      }
      this.resolvedVehicle.set({ id: vehicleSnapshot.id, ...vehicleSnapshot.data() } as Vehicle);
    } catch {
      this.vehicleUnavailable.set(true);
    }
  }

  async editRide() {
    this.dialogRef.close();
    this.dialog.open(EditRideComponent, {
      width: '800px',
      data: {
        ride: this.ride().rideVersion === 2 ? this.ride() : await updateRideToV2(this.ride()),
      },
    });
  }

  async retourRide() {
    this.dialogRef.close();
    let ride = { ...this.ride() };
    const existingRetours = (
      await getDocs(
        query(
          collection(db, `organisations/${this.organisationId()}/rides`),
          where('retourParentId', '==', ride.id),
        ),
      )
    ).docs;
    if (existingRetours.length > 0) {
      ride = existingRetours[0].data() as Ride;
    }
    this.dialog.open(EditRideComponent, {
      width: '800px',
      data: {
        ride: ride.rideVersion === 2 ? ride : await updateRideToV2(ride),
        retourRide: true,
      },
    });
  }

  async deleteRide() {
    if (this.deleting()) {
      return;
    }
    this.deleting.set(true);

    const settings = this.organisationNotificationSettings();
    if (!settings) {
      this.deleting.set(false);
      return;
    }

    let notificationBool =
      (settings.email.enabled && settings.email.RideDeleted) ||
      (settings.sms.enabled && settings.sms.RideDeleted);

    if (!this.users() || this.users().length === 0) {
      notificationBool = false;
    }

    const noDeleteReason = dataStore.generalSettings.noDeleteReason ?? false;
    const deleteRef = this.dialog.open(DeleteRideComponent, {
      width: '600px',
      data: {
        notification: notificationBool,
        noDeleteReason: noDeleteReason,
      },
    });

    deleteRef.afterClosed().subscribe(async (deleteResult: any) => {
      if (deleteResult !== false) {
        if (this.ride().parentRideId) {
          const deleteCopiesRef = this.dialog.open(CopyUpdateComponent, {
            width: '600px',
            data: {
              oldRide: structuredClone(this.ride()),
              delete: true,
              deleteReason: deleteResult,
            },
          });
          deleteCopiesRef.afterClosed().subscribe(async (copiesDeleted) => {
            if (copiesDeleted === undefined) {
              this.deleting.set(false);
              return;
            }
            await this.handleDeleteRide(notificationBool, deleteResult, copiesDeleted);
          });
        } else {
          await this.handleDeleteRide(notificationBool, deleteResult);
        }
      } else {
        this.deleting.set(false);
      }
    });
  }

  async handleDeleteRide(
    notificationBool: boolean,
    deleteReason: string,
    copiesDeleted: boolean = false,
  ) {
    await this.backupRide(deleteReason);
    await deleteDoc(doc(db, `organisations/${this.organisationId()}/rides/${this.ride().id}`));
    if (notificationBool) {
      this.notifyParticipants(this.ride().id);
    } else {
      this.dialogRef.close();
    }
    if (copiesDeleted) {
      this.snackBar.open('De ritten zijn verwijderd.', 'X', {
        duration: 5000,
      });
    } else {
      this.snackBar.open('De rit is verwijderd.', 'X', {
        duration: 5000,
      });
    }
  }

  async notifyParticipants(rideId: string) {
    const settings = this.organisationNotificationSettings();
    if (settings?.email.RideDeleted || settings?.sms.RideDeleted) {
      const dialogRef = this.dialog.open(NotifyUsersRideDialog, {
        width: '600px',
        disableClose: true,
        data: { deleted: true },
      });
      dialogRef.afterClosed().subscribe(async (result) => {
        if (result) {
          for (const user of this.users()) {
            deleteRideNotificationParticipant(user, this.organisationId());
          }
        }
        for (const user of this.users()) {
          removeFromReminderQueue(rideId, user, this.organisationId());
        }
        this.deleting.set(false);
        this.dialogRef.close();
      });
    } else {
      for (const user of this.users()) {
        removeFromReminderQueue(rideId, user, this.organisationId());
      }
      this.deleting.set(false);
      this.dialogRef.close();
    }
  }

  async backupRide(deleteReason: string) {
    const backupRide = {} as any;
    backupRide.date = new Date();
    backupRide.doneByUser = this.userEmail();
    backupRide.doneByUid = this.userId();
    backupRide.deleteReason = deleteReason;
    backupRide.rideData = { ...this.ride() };
    await setDoc(
      doc(db, `eventLogs/${this.organisationId()}/deletedRides/${this.ride().id}`),
      backupRide,
    );
    const editDocs = await getDocs(
      collection(db, `organisations/${this.organisationId()}/rides/${this.ride().id}/edits`),
    );
    editDocs.forEach((editDoc) => {
      setDoc(
        doc(
          db,
          `eventLogs/${this.organisationId()}/deletedRides/${this.ride().id}/edits/${editDoc.id}`,
        ),
        editDoc.data(),
      );
      deleteDoc(editDoc.ref);
    });
    const scanDocs = await getDocs(
      collection(db, `organisations/${this.organisationId()}/rides/${this.ride().id}/scans`),
    );
    scanDocs.forEach((scanDoc) => {
      deleteDoc(scanDoc.ref);
    });
  }

  async copyRide() {
    const vehicleError = await getRideCopyVehicleError(this.organisationId(), this.ride());
    if (vehicleError) {
      this.dialog.open(WarningDialog, {
        data: { dialogType: 'rideCopyBlocked', optionalData: { exceptionName: vehicleError } },
      });
      return;
    }

    const copyDialogRef = this.dialog.open(CopyRideComponent, {
      width: '500px',
      data: { ride: structuredClone(this.ride()), vehicle: this.data.vehicle },
    });
    copyDialogRef.afterClosed().subscribe((result) => {
      if (result === false) {
        this.dialog.open(RideDetailsComponent, {
          width: '800px',
          data: {
            ride: structuredClone(this.ride()),
            vehicle: this.resolvedVehicle() ?? this.data.vehicle,
          },
        });
      }
    });
    this.dialogRef.close();
  }

  viewEdits() {
    this.dialog.open(RideEditsComponent, {
      width: '500px',
      data: {
        ride: this.ride(),
        vehicle: this.data.vehicle,
      },
      autoFocus: false,
    });
  }
}
